import React, { useState, useEffect } from "react";
import {
  Plus,
  Folder,
  MoreVertical,
  Trash2,
  Eye,
  Settings,
  Cog,
  X,
  FolderOpen,
  Search,
} from "lucide-react";
import { Menu, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import SuiteSettingsModal from "./SuiteSettingsModal";
import {
  useCreateApiSuite,
  useGetApiSuites,
  useDeleteApiSuite,
  useGetEnvironmentsQuery,
  useSetEnvForSuite,
  useRemoveEnvForSuite,
} from "../../../useQuery";
import { useNotification } from "../../../hooks";

const ApiSuite = ({ onSuiteSelect }) => {
  const notify = useNotification();
  const [suiteName, setSuiteName] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [openDialog, setOpenDialog] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedSuite, setSelectedSuite] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, suite: null });
  const [settingsDialog, setSettingsDialog] = useState({ open: false, suite: null });
  const [environmentDialog, setEnvironmentDialog] = useState({ open: false, suite: null });
  const [removeEnvDialog, setRemoveEnvDialog] = useState({ open: false, suite: null });

  const { mutate: fetchSuites, data: suites, isPending: loading } = useGetApiSuites();
  const { data: environments, isLoading: environmentsLoading } = useGetEnvironmentsQuery();

  const { mutate: setEnvironment } = useSetEnvForSuite({
    onSuccess: () => {
      notify("Environment set successfully!", "success");
      setEnvironmentDialog({ open: false, suite: null });
      fetchSuites();
    },
    onError: () => notify("Error setting environment", "error"),
  });

  const { mutate: removeEnvironment, isPending: removingEnvironment } = useRemoveEnvForSuite({
    onSuccess: () => {
      notify("Environment removed successfully!", "success");
      setRemoveEnvDialog({ open: false, suite: null });
      fetchSuites();
    },
    onError: () => notify("Error removing environment", "error"),
  });

  const { mutate: createSuite, isPending: creating } = useCreateApiSuite({
    onSuccess: () => {
      notify("Suite Created Successfully!", "success");
      setSuiteName("");
      setOpenDialog(false);
      fetchSuites();
    },
  });

  const { mutate: deleteSuite, isPending: deleting } = useDeleteApiSuite({
    onSuccess: () => {
      notify("Suite Deleted Successfully!", "success");
      setDeleteDialog({ open: false, suite: null });
      fetchSuites();
    },
    onError: () => {
      notify("Error Deleting Suite", "error");
      setDeleteDialog({ open: false, suite: null });
    },
  });

  useEffect(() => {
    fetchSuites();
  }, [fetchSuites]);

  const filteredSuites = (suites || []).filter((s) =>
    s.testSuiteName?.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const handleCreate = () => {
    if (suiteName.trim()) createSuite(suiteName.trim());
  };
  const handleMenuOpen = (e, suite) => {
    setAnchorEl(e.currentTarget);
    setSelectedSuite(suite);
  };
  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedSuite(null);
  };
  const handleDelete = () => {
    setDeleteDialog({ open: true, suite: selectedSuite });
    handleMenuClose();
  };
  const confirmDelete = () => {
    if (deleteDialog.suite) deleteSuite(deleteDialog.suite.id);
  };
  const handleManage = () => {
    if (selectedSuite && onSuiteSelect) onSuiteSelect(selectedSuite);
    handleMenuClose();
  };
  const handleCardClick = (suite) => {
    if (onSuiteSelect) onSuiteSelect(suite);
  };
  const handleSetEnvironment = () => {
    setEnvironmentDialog({ open: true, suite: selectedSuite });
    handleMenuClose();
  };
  const confirmSetEnvironment = (envId) => {
    if (environmentDialog.suite)
      setEnvironment({ testSuiteId: environmentDialog.suite.id, environmentId: envId });
  };
  const handleRemoveEnvironment = () => {
    setRemoveEnvDialog({ open: true, suite: selectedSuite });
    handleMenuClose();
  };
  const confirmRemoveEnvironment = () => {
    if (removeEnvDialog.suite) removeEnvironment({ testSuiteId: removeEnvDialog.suite.id });
  };

  const dialogPaperProps = {
    style: {
      fontFamily: "inherit",
      borderRadius: "12px",
      boxShadow: "0 20px 60px rgba(0,0,0,0.12)",
    },
  };

  if (loading && (!suites || suites.length === 0)) {
    return (
      <div className="font-apple bg-white min-h-screen flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
          <span className="text-sm text-gray-500">Loading suites...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="font-apple bg-gray-50 min-h-screen">
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">API Test Suites</h2>
            <p className="text-sm text-gray-500 mt-0.5">
              Manage and organize your API test collections
            </p>
          </div>
          <button
            onClick={() => setOpenDialog(true)}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 transition-colors cursor-pointer rounded-lg shadow-sm"
          >
            <Plus size={15} />
            Create API Suite
          </button>
        </div>

        {suites && suites.length > 0 && (
          <div className="mt-4 relative max-w-sm">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search suites..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg bg-gray-50 focus:outline-none focus:border-blue-400 focus:bg-white transition-colors"
            />
          </div>
        )}
      </div>

      <div className="p-6">
        {!suites || suites.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-24 text-center">
            <div className="w-16 h-16 bg-blue-50 rounded-2xl flex items-center justify-center mb-4">
              <FolderOpen size={28} className="text-blue-500" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No API suites yet</h3>
            <p className="text-sm text-gray-500 mb-6 max-w-xs">
              Create your first test suite to start organizing and running your API tests.
            </p>
            <button
              onClick={() => setOpenDialog(true)}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 transition-colors cursor-pointer rounded-lg"
            >
              <Plus size={15} />
              Create Your First Suite
            </button>
          </div>
        ) : filteredSuites.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-24 text-center">
            <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center mb-3">
              <Search size={20} className="text-gray-400" />
            </div>
            <p className="text-sm text-gray-500">
              No suites match "<span className="font-medium text-gray-700">{searchQuery}</span>"
            </p>
            <button
              onClick={() => setSearchQuery("")}
              className="mt-2 text-sm text-blue-600 hover:underline cursor-pointer"
            >
              Clear search
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {filteredSuites.map((suite) => (
              <div
                key={suite.id}
                className="bg-white border border-gray-200 hover:border-blue-300 hover:shadow-md cursor-pointer transition-all duration-200 rounded-xl p-5 group"
                onClick={() => handleCardClick(suite)}
              >
                <div className="flex justify-between items-start mb-4">
                  <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center group-hover:bg-blue-100 transition-colors">
                    <Folder size={18} className="text-blue-600" />
                  </div>
                  <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setSettingsDialog({ open: true, suite });
                      }}
                      className="p-1.5 hover:bg-gray-100 rounded-lg cursor-pointer transition-colors"
                      title="Settings"
                    >
                      <Cog size={15} className="text-gray-500" />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleMenuOpen(e, suite);
                      }}
                      className="p-1.5 hover:bg-gray-100 rounded-lg cursor-pointer transition-colors"
                      title="More options"
                    >
                      <MoreVertical size={15} className="text-gray-500" />
                    </button>
                  </div>
                </div>

                <h3 className="text-sm font-semibold text-gray-900 mb-1 line-clamp-2 leading-snug">
                  {suite.testSuiteName}
                </h3>

                <div className="mt-3 flex items-center gap-1.5">
                  {suite.environmentName ? (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-100">
                      {suite.environmentName}
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs text-gray-400 bg-gray-50 border border-gray-100">
                      No environment
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={dialogPaperProps}
      >
        <DialogTitle style={{ fontWeight: 600, fontSize: "1rem", paddingBottom: "4px" }}>
          Create New API Suite
        </DialogTitle>
        <DialogContent style={{ paddingTop: "8px" }}>
          <p className="text-sm text-gray-500 mb-3">
            Give your test suite a descriptive name to identify it later.
          </p>
          <input
            autoFocus
            type="text"
            placeholder="e.g., User Authentication Tests"
            value={suiteName}
            onChange={(e) => setSuiteName(e.target.value)}
            onKeyPress={(e) => e.key === "Enter" && handleCreate()}
            className="w-full px-3 py-2.5 text-sm border border-gray-200 focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-100 cursor-text rounded-lg transition-colors"
          />
        </DialogContent>
        <DialogActions style={{ padding: "12px 24px 20px" }}>
          <button
            onClick={() => setOpenDialog(false)}
            className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 cursor-pointer border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleCreate}
            disabled={!suiteName.trim() || creating}
            className="px-4 py-2 text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 cursor-pointer rounded-lg disabled:opacity-40 transition-colors"
          >
            {creating ? "Creating..." : "Create Suite"}
          </button>
        </DialogActions>
      </Dialog>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          style: {
            fontFamily: "inherit",
            borderRadius: "10px",
            minWidth: "168px",
            border: "1px solid #e5e7eb",
            boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
          },
        }}
      >
        <MenuItem
          onClick={handleManage}
          style={{ fontSize: "0.8125rem", padding: "8px 14px", gap: "8px" }}
        >
          <Eye size={14} className="text-gray-500" />
          <span>Manage Suite</span>
        </MenuItem>
        <MenuItem
          onClick={handleSetEnvironment}
          style={{ fontSize: "0.8125rem", padding: "8px 14px", gap: "8px" }}
        >
          <Settings size={14} className="text-gray-500" />
          <span>Set Environment</span>
        </MenuItem>
        {selectedSuite?.environmentName && (
          <MenuItem
            onClick={handleRemoveEnvironment}
            style={{ fontSize: "0.8125rem", padding: "8px 14px", color: "#dc2626", gap: "8px" }}
          >
            <X size={14} />
            <span>Remove Environment</span>
          </MenuItem>
        )}
        <div style={{ height: "1px", background: "#f3f4f6", margin: "4px 0" }} />
        <MenuItem
          onClick={handleDelete}
          style={{ fontSize: "0.8125rem", padding: "8px 14px", color: "#dc2626", gap: "8px" }}
        >
          <Trash2 size={14} />
          <span>Delete Suite</span>
        </MenuItem>
      </Menu>

      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, suite: null })}
        maxWidth="xs"
        fullWidth
        PaperProps={dialogPaperProps}
      >
        <DialogTitle style={{ fontWeight: 600, fontSize: "1rem" }}>Delete Suite</DialogTitle>
        <DialogContent>
          <p className="text-sm text-gray-600">
            Are you sure you want to delete{" "}
            <span className="font-semibold text-gray-900">
              "{deleteDialog.suite?.testSuiteName}"
            </span>
            ? This action cannot be undone and will remove all associated test results.
          </p>
        </DialogContent>
        <DialogActions style={{ padding: "12px 24px 20px" }}>
          <button
            onClick={() => setDeleteDialog({ open: false, suite: null })}
            className="px-4 py-2 text-sm text-gray-600 cursor-pointer border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={confirmDelete}
            disabled={deleting}
            className="px-4 py-2 text-sm font-medium bg-red-600 text-white hover:bg-red-700 cursor-pointer rounded-lg disabled:opacity-40 transition-colors"
          >
            {deleting ? "Deleting..." : "Delete Suite"}
          </button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={environmentDialog.open}
        onClose={() => setEnvironmentDialog({ open: false, suite: null })}
        maxWidth="sm"
        fullWidth
        PaperProps={dialogPaperProps}
      >
        <DialogTitle style={{ fontWeight: 600, fontSize: "1rem" }}>Set Environment</DialogTitle>
        <DialogContent>
          <p className="text-sm text-gray-500 mb-4">
            Choose an environment for{" "}
            <span className="font-semibold text-gray-800">
              {environmentDialog.suite?.testSuiteName}
            </span>
            . This will be used as the base URL for all requests in this suite.
          </p>
          {environmentsLoading ? (
            <div className="py-6 text-center text-sm text-gray-400">Loading environments...</div>
          ) : !environments || environments.length === 0 ? (
            <div className="py-6 text-center text-sm text-gray-400">
              No environments available. Create one first.
            </div>
          ) : (
            <div className="space-y-2">
              {environments.map((env) => (
                <div
                  key={env.id}
                  onClick={() => confirmSetEnvironment(env.id)}
                  className="flex items-center justify-between px-4 py-3 border border-gray-200 rounded-lg hover:border-blue-300 hover:bg-blue-50 cursor-pointer transition-all"
                >
                  <div>
                    <div className="text-sm font-medium text-gray-900">{env.environmentName}</div>
                  </div>
                  <span className="text-xs text-blue-600 font-medium">Select →</span>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
        <DialogActions style={{ padding: "12px 24px 20px" }}>
          <button
            onClick={() => setEnvironmentDialog({ open: false, suite: null })}
            className="px-4 py-2 text-sm text-gray-600 cursor-pointer border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={removeEnvDialog.open}
        onClose={() => setRemoveEnvDialog({ open: false, suite: null })}
        maxWidth="xs"
        fullWidth
        PaperProps={dialogPaperProps}
      >
        <DialogTitle style={{ fontWeight: 600, fontSize: "1rem" }}>Remove Environment</DialogTitle>
        <DialogContent>
          <p className="text-sm text-gray-600">
            Remove{" "}
            <span className="font-semibold text-gray-900">
              "{removeEnvDialog.suite?.environmentName}"
            </span>{" "}
            from{" "}
            <span className="font-semibold text-gray-900">
              "{removeEnvDialog.suite?.testSuiteName}"
            </span>
            ? The suite will run without environment variables.
          </p>
        </DialogContent>
        <DialogActions style={{ padding: "12px 24px 20px" }}>
          <button
            onClick={() => setRemoveEnvDialog({ open: false, suite: null })}
            className="px-4 py-2 text-sm text-gray-600 cursor-pointer border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={confirmRemoveEnvironment}
            disabled={removingEnvironment}
            className="px-4 py-2 text-sm font-medium bg-red-600 text-white hover:bg-red-700 cursor-pointer rounded-lg disabled:opacity-40 transition-colors"
          >
            {removingEnvironment ? "Removing..." : "Remove Environment"}
          </button>
        </DialogActions>
      </Dialog>

      <SuiteSettingsModal
        open={settingsDialog.open}
        onClose={() => setSettingsDialog({ open: false, suite: null })}
        suite={settingsDialog.suite}
      />
    </div>
  );
};

export default ApiSuite;
